import { format } from "date-fns/format";
import { th as thLocale } from "date-fns/locale/th";

export function formatNumber(n: number | null | undefined, digits = 0): string {
  if (n == null || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("th-TH", {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(n);
}

export function formatPct(n: number | null | undefined, digits = 1): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${formatNumber(n, digits)}%`;
}

export function formatBaht(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `฿${formatNumber(n, 0)}`;
}

/** แปลง YYYY-MM-DD (ปฏิทิน Bangkok) เป็น Date ที่จุดเที่ยง UTC — ใช้กับ date picker */
export function parseBangkokYmdToDate(ymdDash: string): Date {
  const parts = ymdDash.split("-").map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (!y || !m || !d) return new Date();
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
}

/** แสดงวันที่เป็นข้อความ dd/MM/yyyy ชื่อเดือนภาษาไทย */
export function formatBangkokYmdDisplay(ymdDash: string): string {
  try {
    return format(parseBangkokYmdToDate(ymdDash), "dd/MM/yyyy", { locale: thLocale });
  } catch {
    return ymdDash;
  }
}

/** วันนี้ตามปฏิทิน Asia/Bangkok — ใช้เปรียบเทียบเชิงปฏิทินเท่านั้น (ห้ามใช้เป็น upper bound ของ filter) */
export function todayBangkokYmd(): string {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Bangkok" });
}

/**
 * จำนวนวันตามปฏิทินจาก ymd ('YYYYMMDD…' ใช้ 8 ตัวแรก) ถึงวันนี้ตามปฏิทิน Asia/Bangkok
 * 0 = วันนี้ · อนาคต = ติดลบ · ว่าง/รูปแบบผิด = null
 */
export function daysSinceBangkokYmd(ymd: string | null | undefined): number | null {
  if (!ymd) return null;
  const s = String(ymd).slice(0, 8);
  if (!/^\d{8}$/.test(s)) return null;
  const past = Date.UTC(Number(s.slice(0, 4)), Number(s.slice(4, 6)) - 1, Number(s.slice(6, 8)));
  const [ty, tm, td] = todayBangkokYmd().split("-").map(Number);
  return Math.floor((Date.UTC(ty, tm - 1, td) - past) / 86400000);
}

/**
 * เมื่อวานตามปฏิทิน Asia/Bangkok — ใช้เป็น upper bound ของ date filter ทั้งระบบ
 * Sync worker ดึงข้อมูล "เมื่อวาน" ทุกวันเวลา 06:00 ICT → ระบบไม่เคยมีข้อมูลของวันนี้
 * ตั้ง dateEnd = today จะได้กราฟปลายเปิดที่ขาดข้อมูลตรงปลาย ดังนั้นทุก preset ต้อง cap ที่ yesterday
 */
export function yesterdayBangkokYmd(): string {
  return bangkokYmdAddDays(todayBangkokYmd(), -1);
}

/** บวก/ลบวันจาก YYYY-MM-DD ตามปฏิทิน Bangkok */
export function bangkokYmdAddDays(ymdDash: string, deltaDays: number): string {
  const parts = ymdDash.split("-").map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (!y || !m || !d) return ymdDash;
  const utc = Date.UTC(y, m - 1, d, 12, 0, 0);
  const next = new Date(utc + deltaDays * 86400000);
  return next.toLocaleDateString("en-CA", { timeZone: "Asia/Bangkok" });
}

/**
 * บวก/ลบเดือนจาก YYYY-MM-DD ตามปฏิทิน Bangkok — clamp วันที่ถ้าเดือนปลายทางสั้นกว่า
 * เช่น 2026-03-31 ลบ 1 เดือน → 2026-02-28 (ไม่ใช่ 2026-03-03 จาก overflow ของ Date)
 */
export function bangkokYmdAddMonths(ymdDash: string, deltaMonths: number): string {
  const parts = ymdDash.split("-").map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (!y || !m || !d) return ymdDash;
  const targetIdx = (m - 1) + deltaMonths;
  const targetY = y + Math.floor(targetIdx / 12);
  const targetM = ((targetIdx % 12) + 12) % 12 + 1;
  const lastDay = new Date(Date.UTC(targetY, targetM, 0)).getUTCDate();
  const targetD = Math.min(d, lastDay);
  const yy = String(targetY).padStart(4, "0");
  const mm = String(targetM).padStart(2, "0");
  const dd = String(targetD).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/** ช่วงเริ่มต้นแดชบอร์ด: 7 วันที่มีข้อมูลล่าสุด (cap ที่เมื่อวาน Bangkok) */
export function defaultDateRange(): { dateStart: string; dateEnd: string } {
  const end = yesterdayBangkokYmd();
  return { dateStart: bangkokYmdAddDays(end, -6), dateEnd: end };
}

export type DatePresetId =
  | "today"
  | "yesterday"
  | "last7"
  | "last30"
  | "thisMonth"
  | "lastMonth"
  | "last3Months"
  | "custom";

/**
 * ช่วงวันที่ตามปุ่ม preset
 * Convention: ปลายทาง = "เมื่อวาน" (D-1) ตามปฏิทิน Bangkok เพราะ cron sync ดึงเฉพาะวันที่ปิดแล้ว
 * ยกเว้น lastMonth = เต็มเดือนก่อน (ปิดอยู่แล้ว — ไม่ต้อง cap)
 * Edge case: thisMonth ถ้าวันนี้ = วันที่ 1 → เมื่อวานอยู่เดือนก่อน → snap เป็นช่วงวันเดียว (วันที่ 1 ของเดือนนี้)
 */
export function rangeForPreset(
  preset: Exclude<DatePresetId, "custom">
): { dateStart: string; dateEnd: string } {
  const today = todayBangkokYmd();
  const yest = yesterdayBangkokYmd();
  switch (preset) {
    case "today":
      return { dateStart: today, dateEnd: today };
    case "yesterday":
      return { dateStart: yest, dateEnd: yest };
    case "last7":
      return { dateStart: bangkokYmdAddDays(yest, -6), dateEnd: yest };
    case "last30":
      return { dateStart: bangkokYmdAddDays(yest, -29), dateEnd: yest };
    case "thisMonth": {
      const firstOfMonth = `${today.slice(0, 7)}-01`;
      // ถ้าเมื่อวานอยู่เดือนก่อน (เช่นวันนี้คือวันที่ 1) → snap เป็นวันเดียว เพื่อไม่ให้ start > end
      const dateEnd = yest >= firstOfMonth ? yest : firstOfMonth;
      return { dateStart: firstOfMonth, dateEnd };
    }
    case "lastMonth": {
      const lastDayPrev = bangkokYmdAddDays(`${today.slice(0, 7)}-01`, -1);
      const firstDayPrev = `${lastDayPrev.slice(0, 7)}-01`;
      return { dateStart: firstDayPrev, dateEnd: lastDayPrev };
    }
    case "last3Months":
      return { dateStart: bangkokYmdAddDays(yest, -89), dateEnd: yest };
    default:
      return { dateStart: bangkokYmdAddDays(yest, -6), dateEnd: yest };
  }
}

/** เปรียบเทียบช่วงปัจจุบันกับ preset — ถ้าไม่ตรงถือว่ากำหนดเองจากปฏิทิน */
export function detectDatePreset(dateStart: string, dateEnd: string): DatePresetId {
  const order: Exclude<DatePresetId, "custom">[] = [
    "today",
    "yesterday",
    "last7",
    "last30",
    "thisMonth",
    "lastMonth",
    "last3Months",
  ];
  for (const p of order) {
    const r = rangeForPreset(p);
    if (r.dateStart === dateStart && r.dateEnd === dateEnd) return p;
  }
  return "custom";
}

/** แปลงเวลาเป็น YYYY-MM-DD ตามปฏิทิน Bangkok — ใช้กับตัวเลือกวันที่ใน UI */
export function toYmd(d: Date): string {
  return d.toLocaleDateString("en-CA", { timeZone: "Asia/Bangkok" });
}

/**
 * แสดงเวลาจากคอลัมน์ D1 เช่น sync_log.created_at
 * ค่าใน DB จาก `datetime('now')` ของ D1/SQLite คือ UTC — แปลงเป็นนาฬิกาไทย (+7) ให้ตรงกับการตรวจสอบ
 */
export function formatDbUtcAsBangkok(createdAt: string | null | undefined): string {
  if (createdAt == null || createdAt === "") return "—";
  const t = createdAt.trim();
  const normalized = t.includes("T") ? t : t.replace(" ", "T");
  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(normalized);
  const iso = hasTz ? normalized : `${normalized}Z`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return createdAt;
  return d.toLocaleString("th-TH", {
    timeZone: "Asia/Bangkok",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

/** จำนวนวันแบบรวมปลายทั้งสอง (YYYY-MM-DD ตามปฏิทิน Bangkok) — ใช้หารเฉลี่ยต่อวัน */
export function countInclusiveBangkokDays(ymdStart: string, ymdEnd: string): number {
  const a = parseBangkokYmdToDate(ymdStart).getTime();
  const b = parseBangkokYmdToDate(ymdEnd).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return 1;
  const days = Math.floor((b - a) / 86400000) + 1;
  return Math.max(1, days);
}

/** ช่วง YYYY-MM-DD ข้ามปีปฏิทินหรือไม่ — ใช้ตัดสินใจแสดงปีบนแกนกราฟ */
export function dateRangeSpansMultipleYears(dateStart: string, dateEnd: string): boolean {
  const y1 = dateStart.slice(0, 4);
  const y2 = dateEnd.slice(0, 4);
  return y1.length === 4 && y2.length === 4 && y1 !== y2;
}

/**
 * ป้ายแกน X รายวันจาก YYYYMMDD (เช่น payment_date / date_key)
 * ช่วงเดียวกัน: ดด/มม · ข้ามปี: ดด/มม/ปปปป
 */
export function chartAxisLabelFromCompactYmd(compact: string, showFullYear: boolean): string {
  const s = compact.trim();
  if (s.length < 8 || !/^\d{8}/.test(s)) return s;
  const y = s.slice(0, 4);
  const mm = s.slice(4, 6);
  const dd = s.slice(6, 8);
  if (showFullYear) return `${dd}/${mm}/${y}`;
  return `${mm}/${dd}`;
}

/** รายวัน YYYYMMDD ตั้งแต่ start ถึง end รวมปลาย (ปฏิทิน Bangkok) — ใช้เติมกราฟรายวัน */
/** อ่าน dateStart / dateEnd จาก query string (YYYY-MM-DD Bangkok) — คืน null ถ้าไม่สมบูรณ์ */
export function parseBangkokDateRangeQuery(
  dateStart: string | null | undefined,
  dateEnd: string | null | undefined
): { dateStart: string; dateEnd: string } | null {
  const ds = dateStart?.trim();
  const de = dateEnd?.trim();
  if (!ds || !de) return null;
  const re = /^\d{4}-\d{2}-\d{2}$/;
  if (!re.test(ds) || !re.test(de)) return null;
  if (ds > de) return null;
  return { dateStart: ds, dateEnd: de };
}

export function eachCompactYmdInRange(ymdStartDash: string, ymdEndDash: string): string[] {
  if (!ymdStartDash?.trim() || !ymdEndDash?.trim()) return [];
  if (ymdStartDash > ymdEndDash) return [];
  const out: string[] = [];
  let cur = ymdStartDash;
  for (;;) {
    out.push(cur.replace(/-/g, ""));
    if (cur === ymdEndDash) break;
    cur = bangkokYmdAddDays(cur, 1);
  }
  return out;
}

// ---- ตัวจัดรูปวันที่แบบข้อความล้วน (ไม่ขึ้นกับ timezone) — ย้ายมาจากหน้า ad-accounts / player-detail / sync ----

/** YYYY-MM-DD → DD/MM/YYYY · ส่งค่าเดิมกลับถ้า format แปลก */
export function toDmy(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : ymd;
}

/** 'YYYYMMDD' (หรือยาวกว่า เช่น YYYYMMDDhhmmss) → 'YYYY-MM-DD' · ว่าง → "—" · รูปแบบอื่นคืนค่าเดิม */
export function fmtYmdDash(ymd: string | null | undefined): string {
  if (!ymd) return "—";
  const s = String(ymd).slice(0, 8);
  if (!/^\d{8}$/.test(s)) return String(ymd);
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
}

/** 'YYYYMMDD' → 'DD/MM' (หัวคอลัมน์สั้น) · ความยาวไม่ใช่ 8 คืนค่าเดิม */
export function ymdDisplay(ymd: string): string {
  if (ymd.length !== 8) return ymd;
  return `${ymd.slice(6, 8)}/${ymd.slice(4, 6)}`;
}

/** 'YYYYMMDD' → 'YYYY-MM-DD' (สำหรับ ThaiDateField) · คืน '' ถ้าไม่ใช่รูปแบบ 8 หลัก */
export function ymdToDash(ymd: string | null | undefined): string {
  const s = (ymd ?? "").trim();
  return /^\d{8}$/.test(s) ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : "";
}
